import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import styled from '@emotion/styled'
import { TableHead } from '@mui/material'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import EditCalendarOutlinedIcon from '@mui/icons-material/DateRange'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import DocumentIcon from '../../../../assets/icons/Documents/Document.svg'
import VideoLessonIcon from '../../../../assets/icons/VideoLesson.svg'
import LockIcon from '../../../../assets/icons/Lock.svg'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import EditCourseTopicScheduleDialog from './EditCourseTopicScheduleDialog'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import {
    ActiveCourseTopicState,
    CourseState,
    SubjectState,
} from '../../../../features/types'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    activeCourseTopicsData: ActiveCourseTopicState[]
    course?: CourseState
    subject?: SubjectState
    activeTopicsPage: number
    activeTopicsRowsPerPage: number
    isFetching: boolean
    setActiveTopicsPage: (page: number) => void
    setActiveTopicsRowsPerPage: (rowsPerPage: number) => void
    onDelete: (subject: number) => void
}

const ActiveCourseTopicsTable: React.FC<Props> = ({
    activeCourseTopicsData = [],
    course = null,
    subject = null,
    activeTopicsPage,
    activeTopicsRowsPerPage,
    isFetching,
    setActiveTopicsPage,
    setActiveTopicsRowsPerPage,
    onDelete,
}) => {
    const { formatMessage } = useIntl()

    const [scheduleTopic, setScheduleTopic] =
        useState<ActiveCourseTopicState | null>(null)

    const formatAccessDates = (row: ActiveCourseTopicState) => {
        if (!row.startDate && !row.endDate) return '-'
        return `${row.startDate || '...'} - ${row.endDate || '...'}`
    }

    const emptyRows =
        activeTopicsPage > 0
            ? Math.max(
                  0,
                  activeTopicsRowsPerPage - activeCourseTopicsData.length,
              )
            : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setActiveTopicsPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setActiveTopicsRowsPerPage(parseInt(event.target.value, 10))
        setActiveTopicsPage(1)
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>#</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleKz' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'titleRu' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'videoLessonCount' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'testCount' })}
                        </TableCell>

                        <TableCell>{formatMessage({ id: 'group' })}</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'monthNumber' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'weekNumber' })}
                        </TableCell>

                        <TableCell>
                            {formatMessage({ id: 'accessDates' })}
                        </TableCell>

                        <TableCell>{formatMessage({ id: 'signin' })}</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'editSchedule' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'delete' })}
                        </TableCell>
                    </TableRow>
                </TableHead>

                {activeCourseTopicsData.length ? (
                    <TableBody>
                        {(activeTopicsRowsPerPage > 0
                            ? activeCourseTopicsData.slice(
                                  (activeTopicsPage - 1) *
                                      activeTopicsRowsPerPage,
                                  (activeTopicsPage - 1) *
                                      activeTopicsRowsPerPage +
                                      activeTopicsRowsPerPage,
                              )
                            : activeCourseTopicsData
                        ).map((row, index) => (
                            <TableRow key={row.courseTopicId}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {index + 1}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.titleKz}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.titleRu}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    <IconText>
                                        <img
                                            src={
                                                row?.videoCount > 0
                                                    ? VideoLessonIcon
                                                    : LockIcon
                                            }
                                            style={{
                                                width:
                                                    row?.videoCount > 0
                                                        ? '16px'
                                                        : '14px',
                                            }}
                                        />
                                        {row?.videoCount > 0 && row?.videoCount}
                                    </IconText>
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    <IconText>
                                        <img
                                            src={
                                                row?.testCount > 0
                                                    ? DocumentIcon
                                                    : LockIcon
                                            }
                                            style={{
                                                width:
                                                    row?.testCount > 0
                                                        ? '16px'
                                                        : '14px',
                                            }}
                                        />
                                        {row?.testCount > 0 && row?.testCount}
                                    </IconText>
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row?.language}
                                </TableCell>

                                <TableCell>{row?.monthNumber || '-'}</TableCell>

                                <TableCell>{row?.weekNumber || '-'}</TableCell>

                                <TableCell>{formatAccessDates(row)}</TableCell>

                                <TableCell>
                                    <Link
                                        to={`${row.courseTopicId}`}
                                        state={{
                                            course: course,
                                            subject: subject,
                                            topic: row,
                                        }}
                                    >
                                        <IconButton
                                            sx={{ background: ui.track }}
                                        >
                                            <img
                                                src={ArrowRightIcon}
                                                alt="edit"
                                            />
                                        </IconButton>
                                    </Link>
                                </TableCell>

                                <TableCell>
                                    <IconButton
                                        sx={{ background: ui.track }}
                                        onClick={() => setScheduleTopic(row)}
                                    >
                                        <EditCalendarOutlinedIcon
                                            fontSize="small"
                                        />
                                    </IconButton>
                                </TableCell>

                                <TableCell
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <ConfirmDelete
                                        confirmAction={() =>
                                            onDelete(row.courseTopicId)
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={12} />
                            </TableRow>
                        )}
                    </TableBody>
                ) : (
                    !isFetching && (
                        <TableBody>
                            <TableRow>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                    colSpan={12}
                                >
                                    {formatMessage({ id: 'noData' })}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    )
                )}

                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={12}
                            count={activeCourseTopicsData.length}
                            rowsPerPage={activeTopicsRowsPerPage}
                            page={activeTopicsPage - 1}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>

            <EditCourseTopicScheduleDialog
                open={!!scheduleTopic}
                handleClose={() => setScheduleTopic(null)}
                topic={scheduleTopic}
            />
        </TableContainer>
    )
}

export default ActiveCourseTopicsTable

const IconText = styled.div`
    display: flex;
    gap: 4px;
    background: ${ui.track};
    border-radius: 10px;
    padding: 4px 8px;
    width: fit-content;
`
